import { useState } from 'react';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { useLocalStorageValue } from '@react-hookz/web';
import { Plus } from '@phosphor-icons/react';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SmartEmailViewer } from '@/components/email-viewer/SmartEmailViewer';
import { inboundEmailToEmailData } from '@/components/email-viewer/utils';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { ArPaymentAllocations } from './ar-payment-allocations';
import { ApplyToInvoiceDialog } from './ApplyToInvoiceDialog';
import { CustomerLink } from '@/components/accounts-receivable/customer-link';
import { Currency, formatCurrency } from '@/components/common/Currency';
import { isManualOrigin } from '@/components/common/OriginReadOnlyBadge';
import { useDeleteArPaymentApplication } from '@/api/rpc/hooks/accounts-receivable/useArPaymentApplications';
import { useToast } from 'src/context/ToastContext';
import { Link } from '@tanstack/react-router';
import type { ArPaymentDetail, ArPaymentLinkedRemittance } from '@erp/rpc';
import { T, useGT } from 'gt-react';

function formatRemittanceChannel(channel: string): string {
  return channel.charAt(0).toUpperCase() + channel.slice(1);
}

function PaymentLinkedRemittances({
  remittances,
}: {
  remittances: ArPaymentLinkedRemittance[];
}) {
  if (remittances.length === 0) {
    return (
      <div className="py-4 text-sm text-muted-foreground text-center">
        <T context="empty state for ar payment linked remittances">
          No linked remittances
        </T>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      {remittances.map((r) => (
        <Link
          key={r.id}
          to="/accounts-receivable/remittances/$remittanceId"
          params={{ remittanceId: r.id }}
          viewTransition
          className="flex items-center justify-between gap-3 rounded border px-3 py-2 text-sm hover:bg-muted/50"
        >
          <div className="min-w-0 flex-1">
            <div className="font-medium text-blue-600 hover:underline truncate">
              {r.statedReferenceNo ??
                r.statedCustomerName ??
                formatRemittanceChannel(r.channel)}
            </div>
            <div className="text-xs text-muted-foreground">
              {new Date(r.receivedAt).toLocaleDateString()} ·{' '}
              {formatRemittanceChannel(r.channel)}
            </div>
          </div>
          <Currency
            value={r.statedTotalAmount}
            className="text-sm font-semibold shrink-0"
            fallback="—"
          />
        </Link>
      ))}
    </div>
  );
}

interface ArPaymentDetailsProps {
  payment: ArPaymentDetail;
}

function formatPaymentMethod(method: string | null): string {
  if (!method) return '—';
  return method
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getStatusBadge(status: string) {
  if (status === 'pending')
    return (
      <Badge variant="secondary">
        <T context="ar">Pending</T>
      </Badge>
    );
  if (status === 'cleared') {
    return (
      <Badge
        variant="outline"
        className="bg-green-50 text-green-800 border-green-200"
      >
        <T context="ar">Cleared</T>
      </Badge>
    );
  }
  if (status === 'reversed') {
    return (
      <Badge className="bg-amber-100 text-amber-800">
        <T context="ar">Reversed</T>
      </Badge>
    );
  }
  if (status === 'voided') {
    return (
      <Badge variant="outline" className="text-muted-foreground">
        <T context="ar">Voided</T>
      </Badge>
    );
  }
  return <Badge variant="outline">{status}</Badge>;
}

function PaymentMetadataPanel({ payment }: ArPaymentDetailsProps) {
  const gt = useGT();
  const { showToast } = useToast();
  const editable = isManualOrigin(payment.originSystem);
  const [applyOpen, setApplyOpen] = useState(false);
  const deleteApplication = useDeleteArPaymentApplication();

  const totalApplied = payment.invoiceApplications.reduce((sum, app) => {
    const amount = app.appliedAmount ? Number(app.appliedAmount) : 0;
    return sum + (Number.isNaN(amount) ? 0 : amount);
  }, 0);
  const paymentAmount = payment.paymentAmount
    ? Number(payment.paymentAmount)
    : 0;
  const unapplied = Math.max(paymentAmount - totalApplied, 0);

  async function handleRemoveAllocation(applicationId: string) {
    try {
      await deleteApplication.mutateAsync({ id: applicationId });
      showToast({
        title: gt('Allocation removed', {
          context: 'toast title, payment allocation removed',
        }),
        color: 'success',
        icon: 'check',
      });
    } catch (err) {
      showToast({
        title: gt('Remove failed', {
          context: 'toast title, allocation remove failed',
        }),
        message: err instanceof Error ? err.message : '',
        color: 'error',
        icon: 'warning',
      });
    }
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base">
              {payment.referenceNumber ??
                gt('(No reference)', {
                  context: 'placeholder when payment has no reference number',
                })}
            </CardTitle>
            {getStatusBadge(payment.status)}
          </div>
          <div className="text-2xl font-bold text-green-600 mt-1">
            <Currency
              value={payment.paymentAmount}
              currency={payment.currencyCode ?? 'USD'}
              fallback="—"
            />
          </div>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="space-y-0.5">
              <div className="text-xs font-medium text-muted-foreground">
                <T>Received Date</T>
              </div>
              <div className="font-medium">
                {payment.paymentDate
                  ? formatDateOnlyString(payment.paymentDate)
                  : '—'}
              </div>
            </div>

            <div className="space-y-0.5">
              <div className="text-xs font-medium text-muted-foreground">
                <T>Customer</T>
              </div>
              {payment.customerId ? (
                <CustomerLink
                  customerId={payment.customerId}
                  customerName={payment.customerName}
                  erpDisplayName={payment.erpDisplayName}
                />
              ) : (
                <div className="font-medium">{payment.customerName ?? '—'}</div>
              )}
            </div>

            <div className="space-y-0.5">
              <div className="text-xs font-medium text-muted-foreground">
                <T>Payment Method</T>
              </div>
              <div>{formatPaymentMethod(payment.paymentMethod)}</div>
            </div>

            {payment.description && (
              <div className="col-span-2 space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  <T>Description</T>
                </div>
                <div>{payment.description}</div>
              </div>
            )}

            <div className="space-y-0.5">
              <div className="text-xs font-medium text-muted-foreground">
                <T>Allocations</T>
              </div>
              <div>{payment.invoiceApplications.length}</div>
            </div>

            <div className="space-y-0.5">
              <div className="text-xs font-medium text-muted-foreground">
                <T>Bank Receipt</T>
              </div>
              {payment.bankTransaction ? (
                <Link
                  to="/accounts-receivable/cash-receipts/$receiptId"
                  params={{ receiptId: payment.bankTransaction.id }}
                  className="font-medium text-blue-600 hover:underline"
                >
                  {formatCurrency(payment.bankTransaction.amount, {
                    currency: payment.currencyCode ?? 'USD',
                  }) || '—'}
                  {payment.bankTransaction.transactionDate
                    ? ` · ${formatDateOnlyString(payment.bankTransaction.transactionDate)}`
                    : ''}
                </Link>
              ) : (
                <Badge
                  variant="outline"
                  className="border-amber-200 bg-amber-50 text-amber-800"
                >
                  <T context="ar">Unreconciled</T>
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Separator />
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">
            <T context="ar">Invoices</T>
          </div>
          {editable && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setApplyOpen(true)}
            >
              <Plus className="mr-1 size-4" weight="bold" />
              <T context="button label, apply payment to an invoice">
                Apply to invoice
              </T>
            </Button>
          )}
        </div>
        <ArPaymentAllocations
          applications={payment.invoiceApplications}
          editable={editable}
          onRemove={handleRemoveAllocation}
          removingId={
            deleteApplication.isPending ? deleteApplication.variables?.id : null
          }
        />
        {editable && (
          <ApplyToInvoiceDialog
            open={applyOpen}
            onOpenChange={setApplyOpen}
            paymentId={payment.id}
            customerId={payment.customerId}
            defaultAmount={unapplied > 0 ? unapplied.toFixed(2) : null}
          />
        )}
      </div>

      <Separator />
      <div className="space-y-2">
        <div className="text-sm font-semibold">
          <T context="ar payment detail remittances section header">
            Remittances
          </T>
        </div>
        <PaymentLinkedRemittances remittances={payment.linkedRemittances} />
      </div>
    </div>
  );
}

export function ARPaymentDetails({ payment }: ArPaymentDetailsProps) {
  const { value: panelSize, set: setPanelSize } = useLocalStorageValue<number>(
    'ar-payment-details-panel-size',
    {
      defaultValue: 50,
      initializeWithValue: true,
    },
  );

  const sourceEmail = payment.sourceEmail;

  if (!sourceEmail) {
    return (
      <div className="flex h-full flex-col px-3 py-3">
        <PaymentMetadataPanel payment={payment} />
      </div>
    );
  }

  return (
    <ResizablePanelGroup direction="horizontal" className="h-full gap-2 px-3">
      <ResizablePanel
        defaultSize={panelSize}
        onResize={(size) => setPanelSize(size)}
        minSize={25}
        className="flex flex-col pr-2"
      >
        <PaymentMetadataPanel payment={payment} />
      </ResizablePanel>
      <ResizableHandle />
      <ResizablePanel minSize={20}>
        <SmartEmailViewer
          emailData={inboundEmailToEmailData(sourceEmail)}
          conversationId={sourceEmail.conversationId}
          defaultTab="email"
          sourceEmailId={sourceEmail.id}
          highlightedEmailIds={[sourceEmail.id]}
        />
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
